import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, LogIn } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const nav = useNavigate();
  const { login, user } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Staff Login · Sky Basket";
    if (user) nav("/admin", { replace: true });
  }, [user, nav]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!identifier || !password) {
      setError("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await login({ identifier, password, rememberMe: remember });
      toast.success("Welcome back!");
      nav("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white"
    >
      {/* Left visual side */}
      <div
        className="relative hidden lg:block overflow-hidden"
        style={{
          background:
            "linear-gradient(150deg, #299fe0 0%, #2b6fbc 40%, #cb05a5 120%)",
        }}
      >
        <div
          className="absolute -top-40 -left-24 w-[520px] h-[520px] rounded-full opacity-40 blur-3xl"
          style={{
            background: "radial-gradient(circle, #ffffff55 0%, transparent 70%)",
          }}
        />
        <div className="relative h-full flex flex-col justify-between p-14 text-white">
          <Link
            to="/"
            data-testid="back-to-site"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm"
          >
            <ArrowLeft size={16} />
            Back to website
          </Link>

          <div>
            <img
              src={BRAND.logo}
              alt={BRAND.fullName}
              className="w-20 h-20 rounded-full ring-4 ring-white/40 shadow-2xl"
            />
            <h1 className="mt-8 font-display font-black text-5xl leading-[1.05] tracking-tight max-w-md">
              Staff tools that stay out of your way.
            </h1>
            <p className="mt-4 text-white/80 max-w-md leading-relaxed">
              Log in to generate passport-size photo sheets and print-ready
              collages in seconds — designed for the counter, not the code
              editor.
            </p>
          </div>

          <div className="text-xs uppercase tracking-[0.28em] text-white/60">
            Sky Basket · Admin Portal
          </div>
        </div>
      </div>

      {/* Right form side */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img
              src={BRAND.logo}
              alt="Sky Basket"
              className="w-11 h-11 rounded-full"
            />
            <div>
              <div className="font-display font-black text-lg">Sky Basket</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#cb05a5] font-semibold">
                Admin Portal
              </div>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] font-semibold text-[#cb05a5]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#cb05a5]" />
            Staff Login
          </div>
          <h2 className="mt-3 font-display font-black text-4xl text-slate-900 tracking-tight">
            Welcome back.
          </h2>
          <p className="mt-2 text-slate-600">
            Log in with your Sky Basket staff credentials.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.22em] font-semibold text-slate-500 mb-2">
                Email or username
              </label>
              <input
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                data-testid="login-identifier"
                autoComplete="username"
                placeholder="you@example.com"
                className="w-full px-4 py-3.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#299fe0] focus:ring-4 focus:ring-[#299fe0]/15 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.22em] font-semibold text-slate-500 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full px-4 py-3.5 pr-12 rounded-xl bg-slate-50 border border-slate-200 focus:border-[#299fe0] focus:ring-4 focus:ring-[#299fe0]/15 outline-none transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
                  aria-label="Toggle password visibility"
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  data-testid="login-remember-me"
                  className="w-4 h-4 rounded border-slate-300 text-[#299fe0] focus:ring-[#299fe0]"
                />
                <span className="text-sm text-slate-700">Remember me</span>
              </label>
              <button
                type="button"
                data-testid="forgot-password"
                onClick={() =>
                  toast.message(
                    "Please contact the store owner to reset your password.",
                  )
                }
                className="text-sm text-[#299fe0] hover:text-[#cb05a5] font-medium"
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-xl bg-red-50 border border-red-100 text-red-700 px-4 py-3 text-sm"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white shadow-lg disabled:opacity-70 hover:-translate-y-0.5 transition-transform"
              style={{
                background:
                  "linear-gradient(135deg, #299fe0 0%, #cb05a5 140%)",
              }}
            >
              <LogIn size={18} />
              {loading ? "Signing in…" : "Log In"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
